import type { ReactNode } from 'react';

export type BadgeTone = 'neutral' | 'accent' | 'ok' | 'info' | 'caution' | 'warning' | 'critical';

const TONES: Record<BadgeTone, string> = {
  neutral: 'border-line bg-surface-3 text-fg-muted',
  accent: 'border-accent/40 bg-accent-soft text-accent',
  ok: 'border-ok/40 bg-ok-soft text-ok',
  info: 'border-info/40 bg-info-soft text-info',
  caution: 'border-caution/50 bg-caution-soft text-caution',
  warning: 'border-warning/50 bg-warning-soft text-warning',
  critical: 'border-critical/50 bg-critical-soft text-critical',
};

/*
 * 实心底色的前景色统一用 `text-fg-inverted`：
 * 浅色主题下深色底配白字，深色主题下亮色底配近黑字。
 * 固定写 `text-white` 在深色主题里会掉到对比度不足（例如亮红底配白字）。
 */
const SOLID: Record<BadgeTone, string> = {
  neutral: 'border-transparent bg-fg-muted text-surface-0',
  accent: 'border-transparent bg-accent text-accent-fg',
  ok: 'border-transparent bg-ok text-fg-inverted',
  info: 'border-transparent bg-info text-fg-inverted',
  caution: 'border-transparent bg-caution text-fg-inverted',
  warning: 'border-transparent bg-warning text-fg-inverted',
  critical: 'border-transparent bg-critical text-fg-inverted',
};

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  /** 用实心底色强调，用于结论、告警等需要一眼看到的场景 */
  solid?: boolean;
  className?: string;
  title?: string;
}

export function Badge({ tone = 'neutral', children, solid = false, className = '', title }: BadgeProps) {
  return (
    <span
      title={title}
      className={[
        'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] leading-4 font-medium whitespace-nowrap',
        solid ? SOLID[tone] : TONES[tone],
        className,
      ].join(' ')}
    >
      {children}
    </span>
  );
}

/** 进度 / 余量指示条，颜色随严重程度变化。 */
export function QualityBar({
  value,
  min = 0,
  max = 100,
  tone = 'ok',
  label,
  ariaLabel,
}: {
  value: number;
  min?: number;
  max?: number;
  tone?: BadgeTone;
  label?: string;
  ariaLabel?: string;
}) {
  const ratio = max === min ? 0 : Math.min(1, Math.max(0, (value - min) / (max - min)));
  return (
    <div className="flex items-center gap-2" title={label}>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3"
        role="progressbar"
        aria-label={ariaLabel ?? label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={Math.round(value)}
      >
        <div
          className={['h-full rounded-full', SOLID[tone]].join(' ')}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
      {label ? <span className="tabular shrink-0 text-[11px] text-fg-muted">{label}</span> : null}
    </div>
  );
}
